/**
 * MUI theme configuration.
 *
 * Editorial system on the ProjectHub palette: Fraunces display serif over cool paper, body
 * in Inter, mono folio labels in IBM Plex Mono. Squared corners, hairline
 * rules instead of elevation. Applies the global rules: no ripple, and icon
 * buttons never carry a background color (hover is a color change only).
 *
 * Font families reference CSS variables set by next/font in the root layout
 * (--font-display, --font-body, --font-mono).
 */
"use client";

import { createTheme } from "@mui/material/styles";
import { colors } from "./colors";
import { fonts } from "./tokens";

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: colors.primary, dark: colors.boost },
    secondary: { main: colors.secondary, dark: colors.secondaryBoost },
    error: { main: colors.error },
    success: { main: colors.success },
    warning: { main: colors.warning },
    info: { main: colors.info },
    background: { default: colors.background, paper: colors.paper },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary },
    divider: colors.divider,
  },
  typography: {
    fontFamily: fonts.body,
    fontSize: 16,
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.65 },
    h1: {
      fontFamily: fonts.heading,
      fontWeight: 340,
      letterSpacing: "-0.015em",
      lineHeight: 0.98,
    },
    h2: {
      fontFamily: fonts.heading,
      fontWeight: 380,
      letterSpacing: "-0.01em",
      lineHeight: 1.05,
    },
    h3: { fontFamily: fonts.heading, fontWeight: 450, letterSpacing: "0" },
    h4: { fontFamily: fonts.heading, fontWeight: 480 },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 4 },
  components: {
    // No ripple anywhere, ever.
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    // Squared editorial buttons; simple color change only, no transform/shadow
    // animation. Contained: ink block that turns violet on hover. Outlined: 1px ink
    // rule that turns violet.
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 2,
          paddingInline: 24,
          paddingBlock: 10,
          transition:
            "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
          "&:hover": { boxShadow: "none", transform: "none" },
        },
        contained: {
          backgroundColor: colors.textPrimary,
          color: colors.background,
          "&:hover": { backgroundColor: colors.primary },
        },
        outlined: {
          borderColor: colors.textPrimary,
          color: colors.textPrimary,
          "&:hover": {
            borderColor: colors.primary,
            color: colors.primary,
            backgroundColor: "transparent",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "transparent",
            color: colors.primary,
          },
        },
      },
    },
    // Links turn violet on hover — color change only, site-wide.
    MuiLink: {
      styleOverrides: {
        root: {
          "&:hover": { color: colors.primary },
        },
      },
    },
    // Ruled, not elevated.
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 4 },
      },
    },
    // Icon buttons: never a background color, hover is violet text only.
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          backgroundColor: "transparent",
          "&:hover, &:focus, &:active": {
            backgroundColor: "transparent",
            color: colors.iconHover,
          },
        },
      },
    },
  },
});
